import React, { useEffect, useMemo, useState } from 'react';
import { Select, Input, Empty } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';
import { NdoePage, NdoePageHeader, NdoeLoading, NdoeError } from '../../components/ndoe';
import './permissionsMatrice.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const ROLE_LABEL = { admin: 'Administrateur', secretaire: 'Secrétaire', vendeur: 'Vendeur', livreur: 'Livreur' };

// Matrice de permissions (Phase 2b) — branchée sur l'API :
//  - mode Rôles : coche/décoche un droit par rôle (PUT /role/:role) ;
//  - mode Utilisateur : fait défiler hérité → accordé → refusé (PUT /user/:id/:cle).
const PermissionsMatrice = () => {
  const [catalogue, setCatalogue] = useState(null);   // { modules:[...], roles:[...] }
  const [rolePerms, setRolePerms] = useState({});     // role -> Set(cles)
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);

  const [mode, setMode] = useState('roles');          // 'roles' | 'user'
  const [curUser, setCurUser] = useState(null);       // id
  const [userData, setUserData] = useState(null);     // { user, overrides, effective }
  const [filterMod, setFilterMod] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState({});               // cle busy flag

  const charger = async () => {
    setLoading(true); setErreur(null);
    try {
      const [cat, roles, us] = await Promise.all([
        axios.get(`${DOMAIN}/api/permissions/catalogue`),
        axios.get(`${DOMAIN}/api/permissions/roles`),
        axios.get(`${DOMAIN}/api/user/getUser`),
      ]);
      setCatalogue(cat.data);
      const rp = {};
      Object.entries(roles.data.roles || {}).forEach(([r, cles]) => { rp[r] = new Set(cles); });
      setRolePerms(rp);
      setUsers(Array.isArray(us.data) ? us.data.filter((u) => u.role !== 'admin' || true) : []);
    } catch (e) {
      setErreur(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { charger(); }, []);

  const chargerUser = async (id) => {
    setCurUser(id); setUserData(null);
    if (!id) return;
    try {
      const { data } = await axios.get(`${DOMAIN}/api/permissions/user/${id}`);
      setUserData(data);
    } catch (e) { toast.error(e.response?.data?.error || 'Lecture impossible.'); }
  };

  const modules = catalogue?.modules || [];
  const roles = catalogue?.roles || [];

  const actionsVisibles = useMemo(() => {
    const out = [];
    modules.forEach((m) => {
      if (filterMod && m.id !== filterMod) return;
      m.actions.forEach((a) => {
        const t = (m.label + ' ' + a.libelle).toLowerCase();
        if (q && !t.includes(q.toLowerCase())) return;
        out.push({ m, a });
      });
    });
    return out;
  }, [modules, filterMod, q]);

  // --- Mode rôle : bascule un droit et PUT l'ensemble du rôle ---
  const toggleRole = async (role, cle) => {
    if (busy[`${role}.${cle}`]) return;
    const set = new Set(rolePerms[role] || []);
    if (set.has(cle)) set.delete(cle); else set.add(cle);
    setBusy((b) => ({ ...b, [`${role}.${cle}`]: true }));
    try {
      const cles = [...set];
      await axios.put(`${DOMAIN}/api/permissions/role/${role}`, { cles });
      setRolePerms((rp) => ({ ...rp, [role]: set }));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Modification refusée.');
    } finally { setBusy((b) => ({ ...b, [`${role}.${cle}`]: false })); }
  };

  // --- Mode utilisateur : hérité → accordé → refusé → hérité ---
  const statutUser = (cle) => {
    if (!userData) return 'n';
    const ov = userData.overrides[cle];
    if (ov === 'grant') return 'g';
    if (ov === 'deny') return 'd';
    const inh = rolePerms[userData.user.role]?.has(cle);
    return inh ? 'i' : 'n';
  };
  const cycleUser = async (cle) => {
    if (!userData || busy[cle]) return;
    const cur = userData.overrides[cle];
    const next = cur === undefined ? 'grant' : cur === 'grant' ? 'deny' : 'inherit';
    setBusy((b) => ({ ...b, [cle]: true }));
    try {
      await axios.put(`${DOMAIN}/api/permissions/user/${curUser}/${cle}`, { effet: next });
      setUserData((d) => {
        const overrides = { ...d.overrides };
        if (next === 'inherit') delete overrides[cle]; else overrides[cle] = next;
        return { ...d, overrides };
      });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Modification refusée.');
    } finally { setBusy((b) => ({ ...b, [cle]: false })); }
  };

  const badge = (kind) => {
    const map = { g: ['pm-b g', '✓'], i: ['pm-b i', '✓'], d: ['pm-b d', '✕'], n: ['pm-b n', '·'] };
    const [c, t] = map[kind];
    return <span className={c}>{t}</span>;
  };

  return (
    <NdoePage className="pm-page">
      <NdoePageHeader
        eyebrow="Administration"
        title="Matrice des permissions"
        subtitle="Les droits par rôle, et les exceptions par utilisateur. Chaque modification est enregistrée et tracée immédiatement."
      />

      <div className="pm-bar">
        <div className="pm-seg">
          <button type="button" aria-pressed={mode === 'roles'} onClick={() => setMode('roles')}>Droits par rôle</button>
          <button type="button" aria-pressed={mode === 'user'} onClick={() => setMode('user')}>Exceptions par utilisateur</button>
        </div>
        {mode === 'user' && (
          <Select
            style={{ minWidth: 220 }} placeholder="Choisir un utilisateur" value={curUser}
            onChange={chargerUser} showSearch optionFilterProp="label"
            options={users.map((u) => ({ value: u.id, label: `${u.username} · ${ROLE_LABEL[u.role] || u.role}` }))}
          />
        )}
        <span style={{ flex: 1 }} />
        <Select allowClear placeholder="Tous les modules" style={{ minWidth: 170 }} value={filterMod || undefined}
          onChange={(v) => setFilterMod(v || '')} options={modules.map((m) => ({ value: m.id, label: m.label }))} />
        <Input allowClear prefix={<SearchOutlined />} placeholder="Rechercher…" style={{ maxWidth: 200 }} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="pm-legend">
        {mode === 'roles' ? (
          <>
            <span><span className="pm-b g">✓</span> Autorisé</span>
            <span><span className="pm-b n">·</span> Non autorisé</span>
          </>
        ) : (
          <>
            <span><span className="pm-b i">✓</span> Hérité du rôle</span>
            <span><span className="pm-b g">✓</span> Accordé (exception +)</span>
            <span><span className="pm-b d">✕</span> Refusé (exception −)</span>
            <span><span className="pm-b n">·</span> Non accordé</span>
          </>
        )}
        <span className="pm-sens"><i className="s-h" /> action sensible</span>
      </div>

      {loading ? (
        <NdoeLoading rows={6} />
      ) : erreur ? (
        <NdoeError text={erreur} onRetry={charger} />
      ) : mode === 'user' && !userData ? (
        <div className="ndoe-card" style={{ padding: 24 }}><Empty description="Choisissez un utilisateur pour voir et ajuster ses exceptions." /></div>
      ) : (
        <div className="pm-scroll ndoe-card">
          <table className="pm-table">
            <thead>
              <tr>
                <th className="pm-first">Module · action</th>
                {mode === 'roles'
                  ? roles.map((r) => <th key={r}>{ROLE_LABEL[r] || r}</th>)
                  : <><th>{userData.user.username}</th><th>Origine</th></>}
              </tr>
            </thead>
            <tbody>
              {actionsVisibles.map(({ m, a }, i) => {
                const prevMod = i > 0 ? actionsVisibles[i - 1].m.id : null;
                const head = m.id !== prevMod ? (
                  <tr className="pm-modrow" key={`h-${m.id}`}>
                    <td colSpan={mode === 'roles' ? roles.length + 1 : 3}>{m.label}</td>
                  </tr>
                ) : null;
                const row = (
                  <tr key={a.cle}>
                    <td className="pm-first">
                      <span className={`pm-act ${a.sensibilite}${a.applique === false ? ' pm-act--inactif' : ''}`}>
                        <i className="pm-dot" />
                        <span className="pm-lbl">{a.libelle}</span>
                        <code>{a.cle}</code>
                        {/*
                            UNE CASE QUI N'EMPÊCHE RIEN DOIT LE DIRE (31/08/2026).

                            Mesuré : sur les 78 droits de cet écran, 36 seulement
                            sont une vraie barrière côté serveur. Les 42 autres
                            cachent au mieux une entrée de menu — l'adresse tapée
                            à la main passe quand même. Décocher une de ces cases
                            en croyant fermer une porte est le pire malentendu
                            possible sur un écran de sécurité.

                            Le serveur envoie `applique` (voir SANS_GARDE_SERVEUR
                            côté API) et un test l'empêche de se périmer : le jour
                            où la garde manquante sera posée, la mention
                            disparaîtra d'elle-même.
                        */}
                        {a.applique === false && (
                          <span
                            className="pm-sanseffet"
                            title="Ce droit masque au mieux un menu ou une colonne. Il ne bloque rien côté serveur : quelqu'un qui connaît l'adresse peut passer."
                          >
                            n’empêche rien côté serveur
                          </span>
                        )}
                      </span>
                    </td>
                    {mode === 'roles' ? roles.map((r) => {
                      const on = rolePerms[r]?.has(a.cle);
                      return (
                        <td key={r}>
                          <button type="button" className="pm-cell" disabled={busy[`${r}.${a.cle}`]} onClick={() => toggleRole(r, a.cle)}>
                            {badge(on ? 'g' : 'n')}
                          </button>
                        </td>
                      );
                    }) : (
                      <>
                        <td>
                          <button type="button" className="pm-cell" disabled={busy[a.cle]} onClick={() => cycleUser(a.cle)}>
                            {badge(statutUser(a.cle))}
                          </button>
                        </td>
                        <td className="pm-origin">
                          {statutUser(a.cle) === 'g' ? 'Exception accordée'
                            : statutUser(a.cle) === 'd' ? 'Exception refusée'
                            : statutUser(a.cle) === 'i' ? 'Hérité du rôle' : '—'}
                        </td>
                      </>
                    )}
                  </tr>
                );
                return <React.Fragment key={a.cle}>{head}{row}</React.Fragment>;
              })}
            </tbody>
          </table>
        </div>
      )}
    </NdoePage>
  );
};

export default PermissionsMatrice;
